import { Link } from "react-router-dom";
import { ArrowRight, Sparkle, Sun, Moon } from "@phosphor-icons/react";
import { useTheme } from "@/context/ThemeContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export default function Landing() {
  const { theme, toggle } = useTheme();
  const [hero, setHero] = useState("https://images.unsplash.com/photo-1527576539890-dfa815648363?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400");
  const [headline, setHeadline] = useState("The client portal, refined in black & white.");
  const [tagline, setTagline] = useState("A calm, monochromatic portal that walks every PGD client through their intake — design forms, brand photos, Zoom intro — with real verification.");
  useEffect(() => {
    api.get("/public/settings").then(r => {
      if (r.data?.landing_hero_image) setHero(r.data.landing_hero_image);
      if (r.data?.landing_headline) setHeadline(r.data.landing_headline);
      if (r.data?.landing_tagline) setTagline(r.data.landing_tagline);
    }).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="landing-page"
         style={{ background: "var(--bg)", color: "var(--text)" }}>
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b" style={{ borderColor: "var(--hair)" }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center font-semibold"
               style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>P</div>
          <div>
            <p className="font-medium leading-none">PrettyGrlsDesign</p>
            <p className="label leading-none mt-1">Client Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={toggle} className="btn-ghost p-2" title="Toggle theme" data-testid="landing-theme-toggle">
            {theme === "dark" ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
          </button>
          <Link data-testid="header-login-link" to="/login" className="btn-ghost text-xs">
            Log in <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-12 gap-0 border-b" style={{ borderColor: "var(--hair)" }}>
        <div className="md:col-span-7 p-8 md:p-16 relative">
          <p className="label mb-6">A PrettyGrlsDesign Portal</p>
          <h1 className="title-xl text-5xl sm:text-6xl md:text-7xl lg:text-8xl" data-testid="landing-headline">
            {headline}
          </h1>
          <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "var(--text-dim)" }} data-testid="landing-tagline">
            {tagline}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link data-testid="hero-start-btn" to="/login" className="btn-primary">
              Enter client portal <ArrowRight size={14} weight="bold" />
            </Link>
            <Link data-testid="hero-admin-link" to="/login" className="btn-ghost">
              Admin sign-in
            </Link>
          </div>
        </div>
        <div className="md:col-span-5 relative border-l-0 md:border-l min-h-[400px]"
             style={{ borderColor: "var(--hair)" }}>
          <img src={hero}
               alt="Editorial" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <p className="label" style={{ color: "rgba(255,255,255,0.7)" }}>Client Portal</p>
            <p className="title-xl text-3xl mt-1">Creating Brands That Last</p>
          </div>
        </div>
      </section>

      <div className="overflow-hidden border-b py-3" style={{ borderColor: "var(--hair)", background: "var(--invert-bg)" }}>
        <div className="marquee-track whitespace-nowrap flex gap-16 label" style={{ color: "var(--invert-text)" }}>
          {Array.from({ length: 6 }).map((_, i) => (
            <span key={i} className="inline-flex items-center gap-3">
              <Sparkle size={14} weight="fill" /> Let's get your brand on the way to success!
            </span>
          ))}
        </div>
      </div>

      <section className="grid grid-cols-1 md:grid-cols-12 gap-6 p-6 md:p-12">
        {[
          { n: "01", t: "Brand Identity, Built With Intention",
            d: "Visual systems, palettes and moodboards that make your brand instantly recognizable — and impossible to ignore." },
          { n: "02", t: "Websites That Actually Convert",
            d: "Custom, fast, mobile-first sites wired for storytelling and sales — Shopify, custom builds, booking sites and landing pages tailored to your offer." },
          { n: "03", t: "Marketing That Grows With You",
            d: "Launch ad campaigns, brand strategy and strategic systems so every touchpoint pulls the customer closer to the checkout." },
        ].map((f) => (
          <div key={f.n} className="md:col-span-4 card-lift p-8" data-testid={`feature-${f.n}`}>
            <p className="label">{f.n}</p>
            <h3 className="title-xl text-2xl mt-4">{f.t}</h3>
            <p className="mt-4 leading-relaxed" style={{ color: "var(--text-dim)" }}>{f.d}</p>
          </div>
        ))}
      </section>

      <footer className="border-t px-6 md:px-12 py-8 flex flex-col md:flex-row justify-between gap-4"
              style={{ borderColor: "var(--hair)" }}>
        <p className="label">© PrettyGrlsDesign — Client Portal</p>
        <p className="label">Branding · Web · Marketing</p>
      </footer>
    </div>
  );
}
